import { useContentForUnit } from '@/features/_data/useContentForUnits';
import { useNFCDeviceConnection } from '@/features/_data/useNFCDeviceConnection';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useParams } from 'react-router-dom';
import { MOCKED_READER } from 'src/utils/envConfig';
import { StoredSlide } from '@/types/SlideType';
import { SlideVideoOptions } from '@/types/SlideVideoOptions';
import { usePreloadedImages } from '@/hooks/usePreloadImages';
import { useCacheAssets } from '@/hooks/useCacheAssets';
import { IconLoading } from '@/icons';
import { Button } from '../../ui-components/Button/Button';
import { PlayerInfoModal } from './PlayerInfoModal/PlayerInfoModal';
import { SlideRendererLoader } from './SlideRendererLoader';
import {
  StyledCardWrapper,
  StyledControllersWrapper,
  StyledEditorWrapper,
  StyledLoadingWrapper,
  StyledSLideRendererPlaceholder,
} from './styles';
import RendererSlideComponent from './RendererSlideComponent';

type RendererSlideType = {
  contentType: string;
  id: string;
  filePath: string;
  videoOptions?: SlideVideoOptions;
};

type SlidesData = {
  type: 'BLANK' | 'SLIDES' | 'LOADING' | 'ESCAPE';
  slides: RendererSlideType[];
  frame: StoredSlide;
};

export const SlideRenderer = () => {
  const { id: unitId } = useParams();
  const [webslideLoading, setWebslideLoading] = useState(false);
  const mockedAvailableTags = ['040F86EA6A5B80', '04DD0BEA6A5B84'];
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [mockedPlacedTags, setMockedPlacedTags] = useState<string[]>([]);
  const [currentTags, setCurrentTags] = useState<
    { tag: string; channel: string }[]
  >([]);

  // without the init state the page will reload randomly
  const [init, setInit] = useState<boolean>(false);
  const [assetUrls, setAssetUrls] = useState<string[]>([]);

  const isNFCDeviceConnected = useNFCDeviceConnection();
  const [frame, setFrame] = useState<StoredSlide | undefined>(undefined);
  const [slides, setSlides] = useState<Array<RendererSlideType>>([]);

  // Define the ref outside of the function
  const videoRef = useRef<HTMLVideoElement>(null);

  const restartVideo = () => {
    if (videoRef.current) {
      videoRef.current.currentTime = 0;
      videoRef.current.play().catch(() => {
        // Error is caught but not handled, effectively ignoring it
        // which is fine in this case since we intentionally want to ignore it
      });
    }
  };
  const handleHKeyPress = (e: KeyboardEvent) => {
    if (e.key.toLowerCase() === 'h') {
      window.electron.ipcRenderer.sendMessage('renderHomeSlide');
    }
  };
  // render Home Screen and get info on initial load
  useEffect(() => {
    window.electron.ipcRenderer.sendMessage('requestNFCPortStatus');
    window.addEventListener('keydown', handleHKeyPress);
    return () => window.removeEventListener('keydown', handleHKeyPress);
  }, []);

  const updateCurrentTags = useCallback((...args: unknown[]) => {
    const tagData = args[0] as {
      value: { action: string; tagId: string; channel: string };
    };

    setCurrentTags((prevTags) => {
      let newCurrentTagList = [...prevTags];
      if (tagData.value.action === 'PASSIVE') {
        newCurrentTagList = newCurrentTagList.filter((tagListData) => {
          return (
            tagListData.tag !== tagData.value.tagId ||
            tagListData.channel !== tagData.value.channel
          );
        });
      } else if (tagData.value.action === 'ACTIVE') {
        newCurrentTagList.push({
          tag: tagData.value.tagId,
          channel: tagData.value.channel,
        });
      }
      return newCurrentTagList;
    });
  }, []);

  useEffect(() => {
    window.electron.ipcRenderer.on('emitTagId', updateCurrentTags);
    return () => {
      window.electron.ipcRenderer.removeListener(
        'emitTagId',
        updateCurrentTags,
      );
    };
  }, [updateCurrentTags]);

  const handleSlidesUpdate = useCallback((...args: unknown[]) => {
    setWebslideLoading(false);
    const slidesData = args[0] as SlidesData; // Assuming the slidesData is the second argument
    if (slidesData.type === 'LOADING') {
      setSlides([]);
      setWebslideLoading(true);
      return;
    }
    if (slidesData.type === 'ESCAPE') {
      setSlides([]);
      setIsModalOpen(true);
      return;
    }
    if (slidesData.type === 'BLANK') {
      setSlides([]);
      return;
    }

    // restart video that is currently playing
    if (slidesData.slides.find((slide) => slide.contentType === 'video/mp4')) {
      restartVideo();
    }

    setSlides(slidesData.slides);
    setFrame(slidesData.frame);
  }, []);

  useEffect(() => {
    window.electron.ipcRenderer.on('emitSlides', handleSlidesUpdate);
    return () => {
      window.electron.ipcRenderer.removeListener(
        'emitSlides',
        handleSlidesUpdate,
      );
    };
  }, [handleSlidesUpdate]);

  useEffect(() => {
    const EscapeSlideMode = (e: KeyboardEvent) => {
      if (
        e.key.toUpperCase() === 'ESCAPE' &&
        !e.altKey &&
        !e.ctrlKey &&
        !e.shiftKey
      ) {
        setIsModalOpen((prevIsModalOpen) => !prevIsModalOpen);
      }
    };

    window.addEventListener('keydown', EscapeSlideMode);
    return () => {
      window.removeEventListener('keydown', EscapeSlideMode);
    };
  }, []);

  useEffect(() => {
    const handleArrowNavigation = (e: KeyboardEvent) => {
      if (e.altKey || e.ctrlKey || e.metaKey) return;

      const target = e.target as HTMLElement | null;
      if (
        target &&
        (target.tagName === 'INPUT' ||
          target.tagName === 'TEXTAREA' ||
          target.isContentEditable)
      ) {
        return;
      }

      if (e.key === 'ArrowRight') {
        e.preventDefault();
        window.electron.sendShowNextSlideInGroup();
      }

      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        window.electron.sendShowPreviousSlideInGroup();
      }
    };

    window.addEventListener('keydown', handleArrowNavigation);
    return () => {
      window.removeEventListener('keydown', handleArrowNavigation);
    };
  }, []);

  useEffect(() => {
    window.electron.ipcRenderer.sendMessage('toggleEscape');
  }, [isModalOpen]);

  useEffect(() => {
    if (!isModalOpen) return;
    const interval = setInterval(() => {
      window.electron.sendRequestNFCPortStatus();
    }, 1000);

    return () => clearInterval(interval);
  }, [isModalOpen]);

  useEffect(() => {
    if (process.env.MOCKED_READER === 'false') return;

    const interval = setInterval(() => {
      window.electron.sendMockedReaderScanResponse(mockedPlacedTags);
    }, 200);

    // eslint-disable-next-line consistent-return
    return () => clearInterval(interval);
  }, [mockedPlacedTags]);

  // cache assets
  const { data, isLoading: isLoadingUnitContent } = useContentForUnit(unitId);
  useEffect(() => {
    if (init) return;
    if (!(data && data.content && data?.content?.length > 0)) return;
    const initialUrls = data.content.flatMap((item) =>
      item.slides
        .filter((slide) =>
          ['video', 'image'].some((type) => slide?.contentType.includes(type)),
        )
        .map((slide) => slide.url),
    );
    setAssetUrls(initialUrls);
    setInit(true);
  }, [data]);

  const preloadedImages = usePreloadedImages(data);

  const { cacheResults, isLoadingCacheResults } = useCacheAssets(assetUrls);

  // Calculate these values with useMemo instead of in an effect
  const { completedItems, totalItems } = useMemo(() => {
    const newTotalItems = cacheResults.length;
    const newCompletedItems = cacheResults.filter(
      (result) => !result.isLoading,
    ).length;

    return {
      completedItems: newCompletedItems,
      totalItems: newTotalItems,
    };
  }, [cacheResults]);

  if (isLoadingUnitContent || isLoadingCacheResults) {
    return (
      <SlideRendererLoader
        testId="slide-renderer-loader"
        completedItems={completedItems}
        totalItems={totalItems}
        isLoadingUnitContent={isLoadingUnitContent}
      />
    );
  }

  if (!slides.length && !isLoadingCacheResults) {
    if (webslideLoading) {
      return (
        <StyledLoadingWrapper data-testid="slide-renderer-loading-wrapper">
          <IconLoading />
        </StyledLoadingWrapper>
      );
    }

    return (
      <>
        <StyledSLideRendererPlaceholder align="center" justify="center" grow>
          <span data-testid="slide-renderer-no-home-slide">
            Place a Trihow on your Trihow Pad to start
          </span>
        </StyledSLideRendererPlaceholder>
        <PlayerInfoModal
          connectionStatus={isNFCDeviceConnected}
          currentTags={currentTags}
          isModalOpen={isModalOpen}
          setIsModalOpen={setIsModalOpen}
        />
      </>
    );
  }

  return (
    <>
      <StyledEditorWrapper data-testid="slide-renderer-wrapper">
        {MOCKED_READER === 'true' ? (
          <StyledControllersWrapper>
            <div style={{ display: 'flex', zIndex: 999, gap: 16 }}>
              {mockedAvailableTags.map((tag) => (
                <Button
                  mode={
                    mockedPlacedTags.includes(tag) ? 'primary' : 'secondary'
                  }
                  key={tag}
                  onClick={() => {
                    setMockedPlacedTags((prev) =>
                      prev.includes(tag)
                        ? prev.filter((currentTag) => tag !== currentTag)
                        : [...prev, tag],
                    );
                  }}
                >
                  {tag}
                </Button>
              ))}
            </div>
          </StyledControllersWrapper>
        ) : null}
        <StyledCardWrapper numberOfSlides={slides.length}>
          {slides.length === 1 ? (
            <RendererSlideComponent
              key={slides[0]?.filePath}
              contentType={slides[0]?.contentType}
              filePath={slides[0]?.filePath}
              videoOptions={
                slides[0]?.videoOptions ? slides[0]?.videoOptions : undefined
              }
              videoRef={videoRef}
              preloadedImage={preloadedImages.get(slides[0]?.filePath)}
              frame={frame}
            />
          ) : null}
          {slides.length > 1
            ? slides.map((currentSlide, index) => (
                <RendererSlideComponent
                  contentType={currentSlide?.contentType}
                  filePath={currentSlide?.filePath}
                  videoRef={videoRef}
                  videoOptions={slides[index]?.videoOptions}
                  preloadedImage={preloadedImages.get(currentSlide?.filePath)}
                />
              ))
            : null}
        </StyledCardWrapper>
      </StyledEditorWrapper>

      <PlayerInfoModal
        connectionStatus={isNFCDeviceConnected}
        currentTags={currentTags}
        isModalOpen={isModalOpen}
        setIsModalOpen={setIsModalOpen}
      />
    </>
  );
};
